import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Hexagon, Calendar, Users, Clock, CheckSquare, Settings,
  BarChart3, LogOut, Menu, X, Plus, MapPin, Edit, Trash2, 
  Loader2, Building2, Phone, ChevronRight, AlertCircle,
  MoreVertical, Info, CalendarDays, Flag as FlagIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { flagSrc } from '../lib/securityWords';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { locationsApi, usersApi, holidaysApi, settingsApi } from '../lib/api';
import { SAME_AS_COMPANY, cityOf, groupOptions, suggestion as zoneSuggestion } from '../lib/officeTimezone';
import WorkingHoursEditor, { computeWeeklyHours } from '../components/WorkingHoursEditor';
import WorkingWeekChangePreview, { WorkingWeekChangePreviewActions }
  from '../components/WorkingWeekChangePreview';
// ⚠️ Pure, and CHECKED — `node src/lib/workingWeek.checks.mjs`. These decide which days
// people work, so they don't live in a page component where clicking is the only test.
import {
  buildWeek, daysWorkedIn, describeDays, inWeekOrder, repricedMessage, weekWhenOverridden,
} from '../lib/workingWeek';
// ⚠️ ONE county list for the whole app — `src/lib/counties.js`. There were three, and
// three counties were spelled differently between them (08/08/2026).
import { COUNTY_REGION, REGION_LABELS, countiesFor } from '../lib/counties';
import CountyField from '../components/CountyField';
import { toast } from 'sonner';


const LocationsPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [locationsEnabled, setLocationsEnabled] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState(null);
  // The head office move, and what it would change. ⚠️ Rob, 08/08/2026: *"moving or
  // changing the head office so easily is good and bad?"* — it reaches the address on
  // invoices and the bank holidays of everyone without an office, so the consequence is
  // shown before it happens rather than discovered afterwards.
  const [headOfficeMove, setHeadOfficeMove] = useState(null);
  const [movingHeadOffice, setMovingHeadOffice] = useState(false);
  
  // ⚠️ NO SET-UP BOX (Rob 3A, 04/11/2026). A 2-step "set up multiple locations" box opened when the
  // switch went on with NO offices — which never happens: sign-up makes a head office, and the
  // backend gives any company without one a head office every time it starts (server.py). It was
  // also broken (Add asked for a country it had no box for). Switching on with no offices just
  // shows the list and "Add location". Its department step went first (F88). BUGS.md 03/11/2026.
  const [toggleLoading, setToggleLoading] = useState(false);
  
  // Countries we can supply holidays for, and which of them have selectable regions.
  // Fetched rather than hard-coded so Phase 3's country list is data, not a deploy.
  const [countries, setCountries] = useState([]);
  const [regionsByCountry, setRegionsByCountry] = useState({});
  // The Timezone box (A9b, 22/10/2026): every real timezone, what "same as the company"
  // currently means, and each country's main timezone for the amber suggestion.
  const [tzOptions, setTzOptions] = useState(null);
  // Everyone, to name the people with no office under the list (Rob 4A, 03/11/2026).
  const [people, setPeople] = useState([]);
  const regionsFor = (code) => (code && regionsByCountry[code]) || [];
  // ⚠️ Fall back to the CODE, never to a blank. "SA usually works Sun to Thu" is
  // usable; " usually works Sun to Thu" looks like a rendering fault.
  const countryName = (code) =>
    countries.find((c) => c.code === code)?.name || code || 'this country';

  // ⚠️ COUNTY IS AN ADDRESS LABEL, NOT A HOLIDAY INPUT (Rob, 02/08/2026).
  // Nothing in the backend reads `county` — it is stored and printed on the address,
  // and that is all. Only `public_holiday_region` decides which days off people get.
  // So outside the UK it does not need to be a controlled list, and deliberately
  // isn't: ISO's GB subdivisions are 221 councils and unitary authorities with no
  // West Midlands, Merseyside or Greater Manchester in them, so adopting a single
  // "proper" worldwide vocabulary would make the one list that works today worse.
  // The UK keeps its postal counties; everywhere else types their own word for it.

  // What this country's working week usually is — fetched when the country changes.
  //
  // ⚠️ NOTHING HERE IS EVER APPLIED AUTOMATICALLY. It renders as a sentence with a
  // button next to it, and the button is the only thing that changes any data. See
  // backend/services/working_weeks.py — this list goes stale silently (the UAE moved
  // in 2022, Johor in 2025) and it decides how much leave people get.
  const [weekSuggestion, setWeekSuggestion] = useState(null);
  // ⚠️ THE WEEK THE ADMIN LAST HAD IN FRONT OF THEM, kept so that flicking between
  // "Same as the company" and "Different at this location" doesn't throw their work
  // away. Rob, 06/08/2026: he set Sun-Thu, switched to the company week to compare,
  // switched back — and had to type it all again.
  //
  // ⚠️ IT IS A DRAFT, NOT A SECOND SOURCE OF TRUTH. `formData.working_hours` is what
  // gets saved; this only ever repopulates the editor when the radio comes back on.
  // Once a location has been SAVED as "same as the company" its own week is genuinely
  // gone — that is what saving meant — and reopening starts from the company week.
  const [lastCustomWeek, setLastCustomWeek] = useState(null);
  // The "what does this change" step, shown between pressing Save and the save
  // happening. ⚠️ It never BLOCKS the save (Rob's decision) — it only makes the
  // consequence visible at the moment the decision is made.
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [suggestionReason, setSuggestionReason] = useState(null);
  const [companyHoursJson, setCompanyHoursJson] = useState(null);

  // The company week, described in words, so "Same as the company" says what that is
  // rather than making the admin go and look.
  const companyWeekSummary = React.useMemo(() => {
    const days = daysWorkedIn(companyHoursJson);
    return days.length ? describeDays(inWeekOrder(days)) : null;
  }, [companyHoursJson]);

  const [formData, setFormData] = useState({
    name: '',
    address_line_1: '',
    address_line_2: '',
    address_line_3: '',
    city: '',
    county: '',
    postcode: '',
    telephone: '',
    country_code: '',
    public_holiday_region: '',
    // null = follow the company. See services/working_weeks.py.
    working_hours: null,
    timezone: ''
  });

  // Whether pressing the primary button will PREVIEW rather than save. Derived in ONE
  // place so the button label and the submit handler can never disagree about it.
  //
  // ⚠️ MUST SIT BELOW `formData` — `const` is hoisted but not initialised, so reading
  // it above the declaration is a ReferenceError that a parse check cannot see. It was
  // above it for about ninety seconds and the file parsed perfectly.
  const willPreview = !!(editingLocation && formData.working_hours
                         && !preview && !previewError);

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  // The edit box's rows (layout A, 03/11/2026): label beside the box from `sm` up, above it on a phone.
  const ROW = 'grid gap-1 sm:grid-cols-[104px_1fr] sm:gap-x-2.5 sm:items-center';
  const ROW_LABEL = 'text-[13px] font-semibold text-muted-foreground';
  const ROW_HINT = 'sm:pl-[114px]';

  useEffect(() => {
    fetchData();
  }, []);

  // ⚠️ REMEMBER THE WEEK BY WATCHING IT, NOT BY REMEMBERING TO SAVE IT.
  //
  // The first attempt called `setLastCustomWeek` from each place that could change the
  // week — the editor, the "use this country's week" button, opening the dialog. Rob
  // reported it still reverting to Monday-to-Friday, and reading the code did not show
  // me which path was missing. **That is the point: an approach whose correctness
  // depends on enumerating every caller is one wrong caller away from this bug, and
  // I had already added a fourth path without noticing.**
  //
  // Watching the value cannot miss a path. Anything that produces a non-empty week
  // gets remembered, whoever set it and whenever.
  useEffect(() => {
    if (formData.working_hours) setLastCustomWeek(formData.working_hours);
  }, [formData.working_hours]);

  // Fetch the country's usual week whenever the chosen country changes.
  //
  // ⚠️ THIS SETS A HINT, NOT THE FORM. It never touches `formData.working_hours` —
  // only the admin clicking "Use Sun to Thu" does that. If you find yourself tempted
  // to apply it here because it saves a click, read the top of
  // backend/services/working_weeks.py first: that click is the whole point.
  useEffect(() => {
    const code = formData.country_code;
    if (!code) { setWeekSuggestion(null); setSuggestionReason(null); return; }
    let cancelled = false;
    locationsApi.getWorkingWeekSuggestion(code)
      .then((res) => {
        if (cancelled) return;
        setWeekSuggestion(res.data?.suggestion || null);
        setSuggestionReason(res.data?.reason || null);
      })
      .catch(() => {
        // A hint that fails to load is not worth a toast — the admin can still set
        // the days by hand, which is the fallback the whole design assumes.
        if (!cancelled) { setWeekSuggestion(null); setSuggestionReason(null); }
      });
    return () => { cancelled = true; };
  }, [formData.country_code]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, locationsRes, countriesRes] = await Promise.all([
        locationsApi.getSettings(),
        locationsApi.list(),
        holidaysApi.getCountries().catch(() => ({ data: { countries: [], regions_by_country: {} } })),
      ]);
      // The company week, so "Same as the company" can say what that actually is
      // instead of making the admin go and look it up on another screen.
      settingsApi.getBusinessHours()
        .then((r) => setCompanyHoursJson(
          typeof r.data === 'string' ? r.data : JSON.stringify(r.data?.business_hours ?? r.data)))
        .catch(() => setCompanyHoursJson(null));
      // ⚠️ Separately and forgivingly — a failure here must not stop the page loading.
      // Without it the box still offers "Same as the company", which is always valid.
      locationsApi.getTimezoneOptions()
        .then((r) => setTzOptions(r.data))
        .catch(() => setTzOptions(null));
      usersApi.list()
        .then((r) => setPeople(r.data || []))
        .catch(() => setPeople([]));
      setCountries(countriesRes.data.countries || []);
      setRegionsByCountry(countriesRes.data.regions_by_country || {});
      setLocationsEnabled(settingsRes.data.multi_location_enabled);
      setLocations(locationsRes.data);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      toast.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  // The on/off switch
  const handleToggleLocations = async () => {
    // Optimistic update - change UI immediately
    const newValue = !locationsEnabled;
    setLocationsEnabled(newValue);
    
    try {
      await locationsApi.updateSettings({ multi_location_enabled: newValue });
      toast.success(newValue ? 'Multi-location mode enabled' : 'Multi-location mode disabled');
    } catch (error) {
      // Revert on error
      setLocationsEnabled(!newValue);
      const message = error.response?.data?.detail || 'Failed to update settings';
      toast.error(message);
    }
  };

  const handleOpenDialog = (location = null) => {
    // ⚠️ NEVER CLOBBER A DIALOG THAT IS ALREADY OPEN.
    //
    // Found 06/08/2026 by logging, after three wrong fixes to a symptom. A click was
    // reaching the location list's Edit button WHILE the edit dialog was open, so this
    // ran again mid-edit, reset `formData` and wiped the working-week draft. The radio
    // handler then read the freshly-cleared state and fell back to Monday-to-Friday —
    // which looked exactly like "the days revert", and sent me hunting in the wrong
    // place three times.
    //
    // The guard is worth having on its own terms, whatever produced the stray click:
    // **re-opening an open form must not silently discard what somebody has typed.**
    if (dialogOpen) return;
    if (location) {
      setEditingLocation(location);
      setFormData({
        name: location.name || '',
        address_line_1: location.address_line_1 || '',
        address_line_2: location.address_line_2 || '',
        address_line_3: location.address_line_3 || '',
        city: location.city || '',
        county: location.county || '',
        postcode: location.postcode || '',
        telephone: location.telephone || '',
        country_code: location.country_code || 'GB',
        public_holiday_region: location.public_holiday_region || '',
        working_hours: location.working_hours || null,
        timezone: location.timezone || ''
      });
      // ⚠️ The server remembers the last week this location had of its own, so
      // switching back to "Different at this location" offers it rather than making
      // the admin retype it. See models.Location.previous_working_hours — it is a
      // convenience and nothing that computes leave reads it.
      setLastCustomWeek(location.working_hours || location.previous_working_hours || null);
      setPreview(null); setPreviewError(false);
    } else {
      setEditingLocation(null);
      setLastCustomWeek(null);
      setPreview(null); setPreviewError(false);
      setFormData({
        name: '',
        address_line_1: '',
        address_line_2: '',
        address_line_3: '',
        city: '',
        county: '',
        postcode: '',
        telephone: '',
        country_code: '',
        public_holiday_region: '',
        working_hours: null,
        timezone: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Location name is required');
      return;
    }
    // Required rather than defaulted — see the note on the field itself. Saving a
    // location with no region is what silently gave Edinburgh staff England's holidays.
    if (!formData.country_code) {
      toast.error('Choose a country — it decides which public holidays staff here get');
      return;
    }
    if (regionsFor(formData.country_code).length > 0 && !formData.public_holiday_region) {
      toast.error('Choose a public holiday region — staff here need the right bank holidays');
      return;
    }

    // ⚠️ SHOW WHAT IT CHANGES BEFORE CHANGING IT — but only when there is something to
    // show: an EXISTING location whose working week is being set. A brand-new location
    // has nobody assigned yet, and following the company week changes nothing.
    if (willPreview) {
      setPreviewLoading(true);
      try {
        const res = await locationsApi.previewWorkingWeek(
          editingLocation.id, formData.working_hours);
        setPreview(res.data);
      } catch (err) {
        // ⚠️ A PREVIEW THAT FAILED MUST NOT LOOK LIKE "NOTHING CHANGES", and it must
        // not stop the admin saving. The panel says so and offers "Save anyway".
        setPreviewError(true);
      } finally {
        setPreviewLoading(false);
      }
      return;
    }

    await reallySave();
  };

  const handleMakeHeadOffice = async (location) => {
    try {
      const { data } = await locationsApi.headOfficePreview(location.id);
      if (!data.differences?.length) {
        // Nothing disagrees, so there is no decision to put in front of anyone.
        await applyHeadOfficeMove(location, null);
        return;
      }
      setHeadOfficeMove({ location, ...data });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not check that location');
    }
  };

  const applyHeadOfficeMove = async (location, direction) => {
    setMovingHeadOffice(true);
    try {
      await locationsApi.makeHeadOffice(location.id, direction);
      // ⚠️ REFETCH RATHER THAN PATCH THE ROW IN PLACE. Moving the flag clears it from
      // every OTHER location, and may have rewritten the company record too — a local
      // edit would leave the screen and the server telling different stories, which is
      // the exact fault this whole workstream has been removing.
      await fetchData();
      setHeadOfficeMove(null);
      toast.success(`${location.name} is now your head office`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not change the head office');
    } finally {
      setMovingHeadOffice(false);
    }
  };

  const reallySave = async () => {
    setFormLoading(true);
    try {
      if (editingLocation) {
        const res = await locationsApi.update(editingLocation.id, formData);
        // ⚠️ CLOSE THE LOOP THE PREVIEW OPENS. The preview named the people who would
        // change; the server re-prices them on save and reports how many. Saying so out
        // loud means an admin can see the promise was kept without going to hunt
        // through balances — and if the two numbers ever disagree, they notice it here
        // rather than a customer noticing it in their leave months later.
        //
        // The count is everyone AT the location, which can be higher than the number
        // the preview flagged as changing — people with their own dated pattern are
        // rechecked and simply don't move. Hence "checked", not "changed".
        toast.success(repricedMessage(res?.data?.repriced_count));
      } else {
        await locationsApi.create(formData);
        toast.success('Location created successfully');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save location';
      toast.error(message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteClick = (location) => {
    setLocationToDelete(location);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!locationToDelete) return;
    
    try {
      await locationsApi.delete(locationToDelete.id);
      toast.success('Location deleted successfully');
      setDeleteDialogOpen(false);
      setLocationToDelete(null);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete location';
      toast.error(message);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };


  // ── What each office follows, in words, for its card (Rob 2A, 03/11/2026) ──────────────
  // ⚠️ A READ-OUT, NOT A RULE. Every line comes from the same fields the editor saves and
  // describes them; nothing here decides anybody's days, holidays or clock. A UK office with no
  // bank holiday region says so in amber rather than pretending (the Perfection Box reports it too).
  const officeFacts = (loc) => {
    const facts = [];
    const code = loc.country_code;
    const regions = regionsFor(code);
    if (!code) {
      facts.push({ key: 'country', Icon: FlagIcon, note: 'No country chosen yet — Edit to choose one', warn: true });
    } else if (regions.length) {
      const region = regions.find((r) => r.code === loc.public_holiday_region);
      facts.push(region
        ? { key: 'country', Icon: FlagIcon, main: countryName(code), note: `${region.name} bank holidays` }
        : { key: 'country', Icon: FlagIcon, main: countryName(code), note: 'no bank holiday region chosen — Edit to choose one', warn: true });
    } else {
      facts.push({ key: 'country', Icon: FlagIcon, main: countryName(code), note: 'public holidays' });  // "United Arab Emirates’s" read badly (Rob 5A, 04/11/2026)
    }
    const ownDays = daysWorkedIn(loc.working_hours);
    facts.push(ownDays.length
      ? (loc.is_head_office
        // A head office with its own week is the old trap (Rob A, 03/11/2026) — say so in amber.
        ? { key: 'week', Icon: CalendarDays, main: describeDays(inWeekOrder(ownDays)), note: 'its own week, not the company’s — Edit to change', warn: true }
        : { key: 'week', Icon: CalendarDays, main: describeDays(inWeekOrder(ownDays)), note: 'this office’s own week' })
      : { key: 'week', Icon: CalendarDays, main: companyWeekSummary || 'The company’s week', note: 'same as the company' });
    const zone = loc.timezone || tzOptions?.company?.value;
    if (zone) {
      const label = (tzOptions?.timezones || []).find((t) => t.value === zone)?.label || '';
      const offset = /\((UTC[^)]*?)\s*now\)/.exec(label);
      facts.push({ key: 'clock', Icon: Clock, main: `${cityOf(zone)} time`,
        note: loc.timezone ? (offset ? `${offset[1]} now` : null) : 'same as the company' });
    }
    const n = loc.user_count || 0;
    facts.push({ key: 'people', Icon: Users, main: n === 1 ? '1 person' : `${n} people` });
    return facts;
  };
  // Active people with no office. Archived people are not "missing an office".
  const noOffice = people.filter((p) => !p.location_id && !p.is_archived && p.is_active !== false);
  // Trimmed: a name saved as "London " read as "London 's bank holidays" (Rob's screen, 03/11/2026).
  const headOfficeName = (locations.find((l) => l.is_head_office)?.name || '').trim() || 'your head office';

  // One office's card (the Locations step). With Multiple locations OFF the head office's card is
  // shown alone, without the ⋮ menu (Rob A, 03/11/2026) — `opts.noMenu`, `opts.people`.
  const renderLocationCard = (location, opts = {}) => {
        const flag = flagSrc(location.country_code);
        const facts = officeFacts(location).map((f) => (
          f.key === 'people' && opts.people ? { ...f, ...opts.people } : f));
        return (
        <div key={location.id} className="card-hive p-5 flex flex-col" data-testid={`location-card-${location.id}`}>
          {/* Name + badge wrap on their own, so Edit and ⋮ stay on the first line on a phone (Rob 5A, 04/11/2026) */}
          <div className="flex items-start gap-2">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 min-w-0 flex-1 pt-1">
            {flag && (
              <img src={flag} alt="" className="w-[22px] h-[16px] rounded-[3px] shadow-[0_0_0_1px_rgba(0,0,0,0.08)] object-cover flex-shrink-0" />
            )}
            <h3 className="font-heading font-semibold text-lg min-w-0 break-words">{location.name}</h3>
            {/* ⚠️ THE FLAG EXISTED FOR MONTHS AND WAS INVISIBLE.
                It already drove a two-way sync with the company
                record, but appeared in no API response and on no
                screen — so when the sync did something an admin
                didn't expect, there was nothing to look at. That is
                how Rob's company sat on Scotland while his London
                head office said England & Wales for who knows how
                long. Decision I, 08/08/2026. */}
            {location.is_head_office && (
              <span
                className="text-[11.5px] font-bold bg-primary text-primary-foreground px-2.5 py-0.5 rounded-full whitespace-nowrap"
                title="Your company address and public holiday region are kept in step with this location"
              >
                Head office
              </span>
            )}
          </div>
            <div className="ml-auto flex items-center gap-1.5 flex-shrink-0">
              <Button 
                variant="outline" 
                size="sm"
                className="bg-card text-foreground hover:bg-secondary"
                onClick={() => handleOpenDialog(location)}
                data-testid={`edit-location-${location.id}`}
              >
                <Edit className="w-3.5 h-3.5 mr-1.5" />
                Edit
              </Button>
              {!opts.noMenu && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="px-2" title="More" aria-label={`More for ${location.name}`} data-testid={`location-more-${location.id}`}>
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  {/* ⚠️ THE HEAD OFFICE HAS NO "MAKE HEAD OFFICE" AND NO
                      WORKING DELETE. The server refuses the delete too — this
                      is the courteous half, so an admin isn't offered an action
                      that will be rejected. Move the flag elsewhere first. */}
                  {!location.is_head_office && (
                    <DropdownMenuItem
                      title="Make this the head office"
                      onClick={() => handleMakeHeadOffice(location)}
                      data-testid={`make-head-office-${location.id}`}
                    >
                      <Building2 className="w-4 h-4 mr-2" />
                      Make this the head office
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem 
                    className="text-destructive focus:text-destructive"
                    disabled={location.is_head_office}
                    title={location.is_head_office
                      ? 'Your company address comes from here — make another location the head office first'
                      : 'Remove this location'}
                    onClick={() => handleDeleteClick(location)}
                    data-testid={`delete-location-${location.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete location
                  </DropdownMenuItem>
                  {location.is_head_office && (
                    <p className="px-2 pb-1.5 pt-0.5 text-xs text-muted-foreground">
                      Make another office the head office first
                    </p>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              )}
            </div>
          </div>
          
          {(location.address_line_1 || location.city) && (
            <p className="text-sm text-muted-foreground mt-2">
              {[location.address_line_1, location.address_line_2, location.address_line_3, location.city, location.county, location.postcode]
                .filter(Boolean)
                .join(', ')}
            </p>
          )}
          
          {location.telephone && (
            <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1">
              <Phone className="w-3.5 h-3.5" />
              {location.telephone}
            </p>
          )}

          {/* What this office follows, so nobody has to open Edit to check (Rob 2A).
              Read from the same fields the editor saves; it decides nothing. */}
          <ul className="mt-3.5 pt-3.5 border-t border-border grid gap-2 text-sm" data-testid={`location-facts-${location.id}`}>
            {facts.map((f) => (
              <li key={f.key} className="flex items-start gap-2.5">
                <f.Icon className="w-4 h-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                <span className={f.warn ? 'font-semibold text-hive-pending-fg dark:text-amber-300' : ''}>
                  {f.main && <span className="font-bold">{f.main}</span>}
                  {f.main && f.note && <span className="text-muted-foreground"> · </span>}
                  {f.note && <span className={f.warn ? '' : 'text-muted-foreground'}>{f.note}</span>}
                </span>
              </li>
            ))}
          </ul>

          {location.is_head_office && (
            <p className="mt-3.5 px-3 py-2 rounded-lg bg-muted text-[13px] flex items-center gap-2">
              <Building2 className="w-4 h-4 flex-shrink-0" />
              Your company address and bank holidays come from here
            </p>
          )}
        </div>
        );
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Access denied</h1>
          <p className="text-muted-foreground">You need admin permissions to access this page.</p>
          <Button className="mt-4" onClick={() => navigate('/dashboard')}>
            Go to the dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Shared Sidebar */}
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      {/* Main content — the Warm Hive look (DESIGN-warm-hive.md, the Locations step, 03/11/2026):
          the Team calendar's header, cards side by side, and what each office follows in view
          (Rob 1A–6A). Every control below does exactly what it did; only where it sits changed. */}
      <main className="flex-1 overflow-auto">
        <header className="relative z-30">
          <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3 px-4 sm:px-6 lg:px-8 pt-6 pb-1">
            <div className="flex items-center gap-4">
              <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
                <Menu className="w-6 h-6" />
              </button>
              <div className="min-w-0">
                <h1 className="font-heading text-2xl sm:text-[30px] sm:leading-tight font-semibold">Locations</h1>
                <p className="text-sm text-muted-foreground mt-0.5">Your offices, and each one&rsquo;s week, holidays and clock</p>
              </div>
            </div>
            
            {locationsEnabled && (
              <Button 
                className="btn-primary" 
                data-testid="add-location-btn"
                onClick={() => handleOpenDialog()}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add location
              </Button>
            )}
          </div>
        </header>

        <div className="p-4 sm:p-6 lg:px-8 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              {/* Multi-location switch */}
              <div className="card-hive px-5 py-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <Label htmlFor="locations-toggle" className="font-heading text-[15px] font-semibold">
                    Multiple locations
                  </Label>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {locationsEnabled 
                      ? 'On. Each office can have its own working week, public holidays and clock. Switching it off hides these screens; nobody’s office, holidays or balance change.'
                      : 'Off. Switch it on if your company works from more than one office, so each can have its own working week, public holidays and clock. Switching on changes nobody\u2019s holidays \u2014 that only happens when you add an office and move people into it.'}
                  </p>
                </div>
                <div className="cursor-pointer flex-shrink-0" style={{ cursor: 'pointer' }}>
                  <Switch
                    id="locations-toggle"
                    checked={locationsEnabled}
                    onCheckedChange={handleToggleLocations}
                    data-testid="locations-toggle"
                    className="cursor-pointer"
                  />
                </div>
              </div>

              {/* ── Multiple locations OFF (Rob, 03/11/2026: "just show A and C if it applies") ──
                  A: what everyone follows — the head office's card, no ⋮ menu; Edit opens the same box.
                  C: offices still saved from before it was switched off, named.
                  ⚠️ "Everyone" only when it's true: switching off is refused while people are at other
                  offices (BUGS.md, 21/10/2026), but older data can still have some — then say how many. */}
              {!locationsEnabled && (() => {
                const ho = locations.find((l) => l.is_head_office);
                const others = locations.filter((l) => !l.is_head_office);
                const elsewhere = others.reduce((n, l) => n + (l.user_count || 0), 0);
                const following = (ho?.user_count || 0) + noOffice.length;
                const plural = (n, one, many) => (n === 1 ? one : many);
                return (
                  <>
                    {ho && (
                      <div className="space-y-4" data-testid="locations-off-head-office">
                        <h2 className="font-heading text-lg sm:text-[19px] font-semibold pt-1">
                          {elsewhere ? 'Your head office' : 'Everyone follows your head office'}
                        </h2>
                        <div className="grid gap-4 sm:gap-5 md:grid-cols-2 min-[1700px]:grid-cols-3">
                          {renderLocationCard(ho, {
                            noMenu: true,
                            people: elsewhere
                              ? { main: `${following} follow${following === 1 ? 's' : ''} it`, note: `${elsewhere} more ${plural(elsewhere, 'is', 'are')} still at other offices` }
                              : { main: 'Everyone', note: plural(following, '1 person', `${following} people`) },
                          })}
                        </div>
                      </div>
                    )}
                    {others.length > 0 && (
                      <div className="card-hive px-5 py-4 flex items-start gap-3 text-sm" data-testid="locations-off-saved">
                        <Info className="w-5 h-5 mt-px flex-shrink-0 text-muted-foreground" />
                        <p>
                          <span className="font-bold">
                            {plural(others.length, 'You have 1 more office saved', `You have ${others.length} more offices saved`)}
                          </span>
                          {' '}&mdash; {others.map((l) => l.name).join(others.length === 2 ? ' and ' : ', ')}.
                          {' '}Switching back on shows {plural(others.length, 'it', 'them')} again, as you left {plural(others.length, 'it', 'them')}.
                        </p>
                      </div>
                    )}
                  </>
                );
              })()}

              {/* Locations list */}
              {locationsEnabled && (
                <>
                  {locations.length === 0 ? (
                    <div className="card-hive py-12 px-6 text-center">
                      <div className="w-14 h-14 mx-auto rounded-2xl bg-secondary flex items-center justify-center mb-4">
                        <MapPin className="w-7 h-7" />
                      </div>
                      <h3 className="font-heading text-lg font-semibold mb-1">No locations yet</h3>
                      <p className="text-muted-foreground mb-4">
                        Add your first office to get started
                      </p>
                      <Button onClick={() => handleOpenDialog()} data-testid="add-first-location-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Add location
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-5">
                      <div className="flex items-center gap-2.5 pt-1">
                        <h2 className="font-heading text-lg sm:text-[19px] font-semibold">
                          Your locations
                        </h2>
                        <span className="text-xs font-bold bg-secondary px-2.5 py-0.5 rounded-full">{locations.length}</span>
                      </div>
                      
                      <div className="grid gap-4 sm:gap-5 md:grid-cols-2 min-[1700px]:grid-cols-3" data-testid="locations-grid">
                      {locations.map((location) => renderLocationCard(location))}
                      </div>

                      {/* People with no office (Rob 4A, names standing out). They resolve to the
                          company: the head office's region (decision I) and the company's week —
                          unless they have their own pattern, which always wins (decision E). */}
                      {noOffice.length > 0 && (
                        <div className="card-hive px-5 py-4 flex items-start gap-3" data-testid="people-without-location">
                          <Info className="w-5 h-5 mt-0.5 flex-shrink-0 text-muted-foreground" />
                          <div className="text-sm leading-relaxed">
                            <p>
                              <span className="font-bold">
                                {noOffice.length === 1 ? '1 person has no location' : `${noOffice.length} people have no location`}
                              </span>
                              {' '}&mdash; they get {headOfficeName}&rsquo;s bank holidays and, unless they have their own pattern, the company&rsquo;s working week.
                            </p>
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {/* Each name opens that person on Employees, on the tab where their
                                  office is chosen (Rob, 03/11/2026). */}
                              {noOffice.map((p) => (
                                <Link
                                  key={p.id}
                                  to={`/employees?edit=${p.id}&step=employment&back=locations`}
                                  title={`Give ${p.first_name} ${p.last_name} an office`}
                                  className="inline-flex items-center rounded-full bg-primary text-primary-foreground text-[13px] font-bold px-3 py-1 hover:bg-hive-burnt dark:hover:bg-hive-orange dark:hover:text-hive-navy transition-colors"
                                  data-testid={`no-location-person-${p.id}`}
                                >
                                  {p.first_name} {p.last_name}
                                </Link>
                              ))}
                            </div>
                            <Link to="/employees" className="inline-block mt-2.5 font-bold text-hive-burnt dark:text-hive-orange hover:underline">
                              Give them an office on Employees &rsaquo;
                            </Link>
                          </div>
                        </div>
                      )}
                      
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </main>

      {/* Add/Edit Location Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        {/*
          ⚠️ THE DIALOG WIDENS WHEN THE HOURS EDITOR IS SHOWN, and it has to.
          `WorkingHoursEditor` sets `minWidth: 912px` on its grid — seven days, two
          sessions, times and actions genuinely need that much. In a `max-w-lg` (512px)
          dialog it simply overflowed off the side of the screen with no way to reach
          it. Rob, 06/08/2026.

          The height cap and the scrolling matter as much: this form was already long
          and the editor doubles it, so without `max-h` the Save button ends up below
          the bottom of the window.
        */}
        <DialogContent
          // ⚠️ ONE WIDTH, NOT TWO. This used to narrow to `max-w-lg` when the working
          // week was set to follow the company, so the whole dialog jumped under the
          // cursor the moment that radio was clicked — and the next click could land
          // on the location list behind it. A form that resizes while you are using it
          // is a bug even when nothing else goes wrong.
          className="max-w-5xl max-h-[90vh] flex flex-col"
          onInteractOutside={(e) => e.preventDefault()}
        >
          {/* The office's own name, flag and badge as the title (Rob 4A, 03/11/2026). */}
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2.5">
              {editingLocation && flagSrc(editingLocation.country_code) && (
                <img src={flagSrc(editingLocation.country_code)} alt="" className="w-[24px] h-[18px] rounded-[3px] shadow-[0_0_0_1px_rgba(0,0,0,0.08)] object-cover" />
              )}
              {editingLocation ? (editingLocation.name || 'Edit location') : 'Add a location'}
              {editingLocation?.is_head_office && (
                <span className="text-[11.5px] font-bold bg-primary text-primary-foreground px-2.5 py-0.5 rounded-full">Head office</span>
              )}
            </DialogTitle>
            <DialogDescription>
              Where it is, the holidays and clock it follows, and its working week
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
            {/* ⚠️ `min-h-0` is load-bearing — a flex child defaults to min-height:auto,
                which refuses to shrink, so the overflow never engages and the dialog
                grows past the viewport instead of scrolling. */}
            <div className="space-y-4 overflow-y-auto min-h-0 flex-1 pr-1">
            {(preview || previewError) ? (
              <WorkingWeekChangePreview preview={preview} error={previewError} />
            ) : (<>
            {/* ⚠️ THE HEAD OFFICE IS ONE RECORD WITH TWO DOORS (decision I) — so say so in the box
                that is one of the doors (Rob 3A, 03/11/2026). These are services/head_office.py's
                MIRRORED_FIELDS: the address lines, city, county, postcode, phone, country, region. */}
            {editingLocation?.is_head_office && (
              <div className="rounded-xl bg-primary text-primary-foreground px-4 py-2.5 text-[13.5px] flex items-center gap-2.5" data-testid="head-office-note">
                <Building2 className="w-4 h-4 flex-shrink-0" />
                <span>
                  <span className="font-bold">This is your head office</span> &mdash; its address, phone,
                  country and region are also your company&rsquo;s (Settings &rarr; General).
                </span>
              </div>
            )}

            {/* Layout A (Rob, 03/11/2026, after "very spread out, not graphical"): two columns,
                labels beside the boxes, the address lines joined, each section a card with its own
                icon. Every field and handler is the one the box always had. */}
            <div className="grid gap-4 md:grid-cols-2 items-start">
            {/* ── The office ───────────────────────────────────────────────────────────────
                ⚠️ COUNTRY STAYS ABOVE THE ADDRESS (Rob, 02/08/2026): it decides whether County is a
                list or a text box, and whether a region is asked for at all. */}
            <section className="card-hive p-4 space-y-1.5">
              <h3 className="flex items-center gap-2.5 font-heading text-[15px] font-semibold mb-2.5">
                <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-hive-burnt/10 text-hive-burnt dark:bg-hive-orange/15 dark:text-hive-orange"><MapPin className="w-4 h-4" /></span>
                The office
              </h3>
              <div className={ROW}>
                <Label htmlFor="name" className={ROW_LABEL}>Name <span className="text-destructive">*</span></Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Portsmouth Office"
                  className="input-hive h-8 text-[13.5px]"
                  data-testid="location-name-input"
                />
              </div>
              <p className={`${ROW_HINT} text-xs text-muted-foreground`}>Office, branch, or where home workers are based.</p>
              <div className={ROW}>
                {/* ⚠️ NO SILENT DEFAULT (Rob, 02/08/2026). Starting empty costs one dropdown;
                    a default nobody changes cost an Edinburgh office its bank holidays. */}
                <Label htmlFor="location_country" className={ROW_LABEL}>Country <span className="text-destructive">*</span></Label>
                <Select
                  value={formData.country_code || ''}
                  onValueChange={(v) => setFormData({
                    ...formData,
                    country_code: v,
                    // Regions and counties both belong to a country, so changing country
                    // must clear them — otherwise a location moved from the UK to Ireland
                    // keeps "scotland" and resolves against a country that has no such
                    // thing, and keeps "Hampshire" on an Irish address where nobody will
                    // ever notice it. Only clear on an ACTUAL change: re-picking the same
                    // country must not wipe answers the admin already gave.
                    public_holiday_region:
                      v === formData.country_code ? formData.public_holiday_region : '',
                    county: v === formData.country_code ? formData.county : '',
                  })}
                >
                  <SelectTrigger id="location_country" className="input-hive h-8 text-[13.5px]">
                    <SelectValue placeholder="Choose…" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[260px]">
                    {countries.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {formData.country_code && formData.country_code !== 'GB' && (
                <p className={`${ROW_HINT} text-xs text-hive-pending-fg dark:text-amber-300`}>
                  Holidays outside the UK come from a community source — check them on the Holidays page.
                </p>
              )}
              <div className={ROW.replace('sm:items-center', 'sm:items-start')}>
                <Label htmlFor="address_line_1" className={`${ROW_LABEL} sm:pt-2`}>Address</Label>
                <div>
                  <Input
                    id="address_line_1"
                    value={formData.address_line_1}
                    onChange={(e) => setFormData({ ...formData, address_line_1: e.target.value })}
                    placeholder="Street address"
                    className="input-hive h-8 text-[13.5px] rounded-b-none"
                  />
                                <Input
                                  id="address_line_2"
                                  aria-label="Address line 2"
                                  value={formData.address_line_2}
                                  onChange={(e) => setFormData({ ...formData, address_line_2: e.target.value })}
                                  placeholder="Line 2"
                  className="input-hive h-8 text-[13.5px] rounded-none -mt-px"
                                />
                                <Input
                                  id="address_line_3"
                                  aria-label="Address line 3"
                                  value={formData.address_line_3}
                                  onChange={(e) => setFormData({ ...formData, address_line_3: e.target.value })}
                                  placeholder="Line 3"
                  className="input-hive h-8 text-[13.5px] rounded-t-none -mt-px"
                                />
                </div>
              </div>
              <div className={ROW}>
                <Label htmlFor="city" className={ROW_LABEL}>Town / city</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="input-hive h-8 text-[13.5px]"
                />
              </div>
                          <CountyField
              className={ROW}
              labelClassName={ROW_LABEL}
              controlClassName="h-8 text-[13.5px] hh-compact"
                            countryCode={formData.country_code}
                            value={formData.county}
                            onChange={(county) => setFormData({ ...formData, county })}
                          />
              <div className={ROW}>
                <Label htmlFor="postcode" className={ROW_LABEL}>Postcode</Label>
                <Input
                  id="postcode"
                  value={formData.postcode}
                  onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                  className="input-hive h-8 text-[13.5px] w-36"
                />
              </div>
              <div className={ROW}>
                <Label htmlFor="telephone" className={ROW_LABEL}>Phone</Label>
                <Input
                  id="telephone"
                  value={formData.telephone}
                  onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                  className="input-hive h-8 text-[13.5px]"
                />
              </div>
            </section>

            <div className="space-y-4">
            {/* ── Holidays and clock ─────────────────────────────────────────────────────── */}
            <section className="card-hive p-4 space-y-1.5">
              <h3 className="flex items-center gap-2.5 font-heading text-[15px] font-semibold mb-2.5">
                <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-purple-500/10 text-purple-600 dark:text-purple-300"><FlagIcon className="w-4 h-4" /></span>
                Holidays and clock
              </h3>
              {/* Region only exists for countries that have them — today, just the UK. */}
              {regionsFor(formData.country_code).length > 0 && (
              <>
              <div className={ROW}>
                <Label htmlFor="public_holiday_region" className={ROW_LABEL}>Bank holidays <span className="text-destructive">*</span></Label>
                <Select
                  value={formData.public_holiday_region || ''}
                  onValueChange={(v) => setFormData({ ...formData, public_holiday_region: v })}
                >
                  <SelectTrigger id="public_holiday_region" className="input-hive h-8 text-[13.5px]">
                    <SelectValue placeholder="Choose…" />
                  </SelectTrigger>
                  <SelectContent>
                    {regionsFor(formData.country_code).map((r) => (
                      <SelectItem key={r.code} value={r.code}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className={`${ROW_HINT} text-xs text-muted-foreground`}>The UK&rsquo;s three regions differ by up to two days a year.</p>
              {COUNTY_REGION[formData.county] && COUNTY_REGION[formData.county] !== formData.public_holiday_region && (
                <p className={`${ROW_HINT} text-xs text-hive-pending-fg dark:text-amber-300`}>
                  {formData.county} is in {REGION_LABELS[COUNTY_REGION[formData.county]]} — its bank holidays differ from your selected region.{' '}
                  <button
                    type="button"
                    className="underline font-medium"
                    onClick={() => setFormData({ ...formData, public_holiday_region: COUNTY_REGION[formData.county] })}
                  >
                    Set region to {REGION_LABELS[COUNTY_REGION[formData.county]]}
                  </button>
                </p>
              )}
              </>
              )}
              {/*
                THE OFFICE'S CLOCK — A9b, agreed by Rob 22/10/2026 (Q16 a, Q19 a).

                Decides when this office's people get their scheduled emails, and when the
                company's Teams/Slack channel gets this office's own "who's off" post.
                "Same as the company" first, stored as nothing (NULL) — the usual answer,
                and what every office had before this box existed.

                ⚠️ THE AMBER LINE IS A SUGGESTION AND NOTHING APPLIES IT — same rule as the
                county -> region note above. A native <select>, like Settings → General's
                company timezone, so typing "New" jumps to New York.
              */}
              {(() => {
                const company = tzOptions?.company;
                const { mine, rest } = groupOptions(tzOptions?.timezones, formData.country_code);
                const hint = zoneSuggestion({
                  officeCountry: formData.country_code,
                  chosen: formData.timezone || SAME_AS_COMPANY,
                  company,
                  timezones: tzOptions?.timezones,
                  mainZones: tzOptions?.main_zones,
                });
                return (
                  <div className="space-y-1.5">
                    <div className={ROW}>
                    <Label htmlFor="location_timezone" className={ROW_LABEL}>Timezone</Label>
                    <select
                      id="location_timezone"
                      value={formData.timezone || SAME_AS_COMPANY}
                      onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                      className="input-hive hh-select hh-compact"
                      data-testid="location-timezone"
                    >
                      <option value={SAME_AS_COMPANY}>
                        {/* The city only — "London (UTC+0 now)" lost its ")" in this box (Rob 5A, 04/11/2026) */}
                        {company ? `Same as the company — ${cityOf(company.value) || company.label}` : 'Same as the company'}
                      </option>
                      {mine.length > 0 && (
                        <optgroup label={`This office's country — ${countryName(formData.country_code)}`}>
                          {mine.map((tz) => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                        </optgroup>
                      )}
                      {rest.length > 0 && (
                        <optgroup label="Everywhere else">
                          {rest.map((tz) => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                        </optgroup>
                      )}
                      {/* A value saved before the list loaded, or one no longer offered,
                          must still SHOW as itself — never as the first option. */}
                      {formData.timezone && !(tzOptions?.timezones || []).some((tz) => tz.value === formData.timezone) && (
                        <option value={formData.timezone}>{cityOf(formData.timezone)}</option>
                      )}
                    </select>
                    </div>
                    <p className={`${ROW_HINT} text-xs text-muted-foreground`}>
                      When this office&rsquo;s people get scheduled emails and its who&rsquo;s-off post.
                    </p>
                    {hint && (
                      <p className={`${ROW_HINT} text-xs text-hive-pending-fg dark:text-amber-300`} data-testid="location-timezone-hint">
                        {hint.text}{' '}
                        <button
                          type="button"
                          className="underline font-medium"
                          onClick={() => setFormData({ ...formData, timezone: hint.zone })}
                        >
                          Use {hint.city}
                        </button>
                        {hint.others ? ' or pick another one from this country above.' : ''}
                      </p>
                    )}
                  </div>
                );
              })()}
            </section>

            {/*
              THE WORKING WEEK — Rob's option B, agreed 06/08/2026 after seeing both
              mocked up side by side.

              "Same as the company" is the first option and the usual answer: a UK
              company with three UK offices should not have to say Monday-to-Friday
              three times, and decision E's chain already says a Location may
              legitimately have no week of its own. NULL means follow the company.

              ⚠️ THE COUNTRY HINT IS A SUGGESTION AND NOTHING APPLIES IT.
              It sits ON the override option and needs a click. `services/working_weeks.py`
              explains why at length — the short version is that this data goes stale
              silently (the UAE moved in 2022, Johor in 2025) and it decides how much
              leave people get.
            */}
            {/* ⚠️ THE HEAD OFFICE USES THE COMPANY'S WEEK (Rob A, 03/11/2026). Rob changed the head
                office's week here and Settings → Working Hours said something else: two weeks, and with
                Multiple locations off the second was invisible. Now it's one fact, like the address.
                A head office that ALREADY has its own week keeps it until the admin chooses — nothing
                changes anybody's balance silently — and the box says so in amber. The server refuses a
                NEW own week for the head office (routers/locations.head_office_week_refusal). */}
            {editingLocation?.is_head_office ? (() => {
              const own = !!formData.working_hours;
              const worked = daysWorkedIn(formData.working_hours || companyHoursJson);
              const hrs = (j) => { const h = computeWeeklyHours(j); return h ? (Number.isInteger(h) ? String(h) : h.toFixed(1)) : null; };
              const ownHrs = hrs(formData.working_hours);
              const coHrs = hrs(companyHoursJson);
              return (
                <section className="card-hive p-4" data-testid="head-office-week">
                  <h3 className="flex items-center gap-2.5 font-heading text-[15px] font-semibold mb-3">
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-primary/10 text-primary"><CalendarDays className="w-4 h-4" /></span>
                    Working week
                  </h3>
                  <div className="flex gap-1.5" aria-label={worked.length ? describeDays(inWeekOrder(worked)) : 'No days'}>
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((d) => (
                      <span key={d} className={`w-[34px] h-[34px] rounded-[9px] flex items-center justify-center text-[12.5px] font-bold ${worked.includes(d) ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'}`}>
                        {d[0].toUpperCase()}
                      </span>
                    ))}
                  </div>
                  {own ? (
                    <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-[13px]" data-testid="head-office-own-week">
                      <p className="text-hive-pending-text dark:text-amber-300">
                        <span className="font-bold">This head office has its own week</span>
                        {ownHrs ? ` (${ownHrs} hours)` : ''}, not the company&rsquo;s{coHrs ? ` (${coHrs} hours)` : ''}.
                        {' '}People here are judged on its week; people with no office on the company&rsquo;s.
                      </p>
                      <button
                        type="button"
                        className="mt-2 font-bold underline text-hive-pending-text dark:text-amber-300"
                        onClick={() => setFormData({ ...formData, working_hours: null })}
                        data-testid="head-office-use-company-week"
                      >
                        Use the company&rsquo;s week instead
                      </button>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-2.5">
                      <span className="font-bold text-foreground">{companyWeekSummary || 'The company\u2019s week'}{coHrs ? `, ${coHrs} hours` : ''}</span>
                      {' '}&mdash; the head office always uses the company&rsquo;s week.{' '}
                      <Link to="/settings?tab=workinghours" className="font-bold text-hive-burnt dark:text-hive-orange hover:underline">Change it in Settings &rarr; Working Hours &rsaquo;</Link>
                      <span className="block mt-1">Anyone here with their own working pattern keeps it.</span>
                    </p>
                  )}
                </section>
              );
            })() : (
            <section className="card-hive p-4">
              <h3 className="flex items-center gap-2.5 font-heading text-[15px] font-semibold mb-3">
                <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-primary/10 text-primary"><CalendarDays className="w-4 h-4" /></span>
                Working week
              </h3>
              {/* The two choices as one switch — still the same two radio buttons underneath. */}
              <div className="inline-flex flex-wrap rounded-[10px] border border-input bg-field p-[3px] gap-[3px]" role="radiogroup" aria-label="Working week">
                <label className={`px-3 py-1.5 rounded-[7px] text-[13px] font-bold cursor-pointer ${!formData.working_hours ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-secondary'}`}>
                  <input
                    type="radio"
                    className="sr-only"
                    checked={!formData.working_hours}
                    onChange={() => setFormData({ ...formData, working_hours: null })}
                  />
                  Same as the company
                </label>
                <label className={`px-3 py-1.5 rounded-[7px] text-[13px] font-bold cursor-pointer ${formData.working_hours ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-secondary'}`}>
                  <input
                    type="radio"
                    className="sr-only"
                    checked={!!formData.working_hours}
                    onChange={() => setFormData({
                      ...formData,
                      working_hours: weekWhenOverridden(
                        formData.working_hours, lastCustomWeek, companyHoursJson),
                    })}
                  />
                  Different here
                </label>
              </div>
              {/* The days it works, as pills — a read-out, not buttons (Rob 2A, 03/11/2026). The
                  times are changed in the hours grid, which opens under both columns. */}
              {(() => {
                const worked = daysWorkedIn(formData.working_hours || companyHoursJson);
                return (
                  <div className="flex gap-1.5 mt-3" data-testid="location-week-days" aria-label={worked.length ? describeDays(inWeekOrder(worked)) : 'No days'}>
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((d) => (
                      <span key={d} className={`w-[34px] h-[34px] rounded-[9px] flex items-center justify-center text-[12.5px] font-bold ${worked.includes(d) ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'}`}>
                        {d[0].toUpperCase()}
                      </span>
                    ))}
                  </div>
                );
              })()}
              <p className="text-xs text-muted-foreground mt-2">
                {formData.working_hours
                  ? (weekSuggestion
                    ? `${countryName(formData.country_code)} usually works ${describeDays(weekSuggestion.days)}. Set the days and times below.`
                    : 'Set the days and times below.')
                  : `${companyWeekSummary || 'The company\u2019s week'}, like the company.`}
                {' '}Anyone here with their own working pattern keeps it.
              </p>
              {formData.working_hours && weekSuggestion && (
                <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
                  <p className="text-xs text-amber-700">
                    {countryName(formData.country_code)} usually works{' '}
                    <strong>{describeDays(weekSuggestion.days)}</strong> — {weekSuggestion.note}
                    {weekSuggestion.since ? ` (since ${weekSuggestion.since})` : ''}.
                  </p>
                  <button
                    type="button"
                    className="mt-2 text-xs underline font-medium text-amber-700"
                    onClick={() => setFormData({
                      ...formData,
                      working_hours: buildWeek(weekSuggestion.days),
                    })}
                  >
                    Use {describeDays(weekSuggestion.days)}
                  </button>
                  <p className="text-xs text-muted-foreground mt-2">
                    Worth checking — this is a general guide, not your company's contract.
                  </p>
                </div>
              )}
              {formData.working_hours && suggestionReason && (
                <p className="text-xs text-muted-foreground mt-2">
                  We deliberately hold no suggestion for {countryName(formData.country_code)}: {suggestionReason}
                </p>
              )}
            </section>
            )}
            </div>
            </div>

            {/* The hours grid needs ~912 px, so it opens across both columns. Never for the head
                office: its week is the company's, changed in Settings → Working Hours. */}
            {formData.working_hours && !editingLocation?.is_head_office && (
              <section className="card-hive p-4">
                <h3 className="font-heading text-[15px] font-semibold mb-3">
                  Days and times{formData.name ? ` at ${formData.name}` : ''}
                </h3>
                <div className="overflow-x-auto min-w-0">
                  <WorkingHoursEditor
                                      hours={formData.working_hours}
                                      onChange={(next) => setFormData({ ...formData, working_hours: next })}
                                    />
                </div>
              </section>
            )}
            </>)}
            </div>

            {/* Outside the scrolling area on purpose: Save stays reachable however
                long the form gets. The preview list scrolls with the body above. */}
            <DialogFooter className="mt-4 pt-4 border-t shrink-0">
              {(preview || previewError) ? (
                <WorkingWeekChangePreviewActions
                  saving={formLoading}
                  error={previewError}
                  onBack={() => { setPreview(null); setPreviewError(false); }}
                  onConfirm={reallySave}
                />
              ) : (<>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              {/*
                ⚠️ THE LABEL MUST SAY WHAT THE BUTTON DOES. When a working week is set
                on an existing location, pressing this shows the preview — it does NOT
                save. Calling it "Save changes" in that state is a small lie, and Rob
                read it exactly that way: *"a user could press save and think it is
                saved rather than there is another step"*.
              */}
              <Button type="submit" disabled={formLoading || previewLoading} data-testid="save-location-btn">
                {(formLoading || previewLoading) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {willPreview
                  ? 'Review who this affects'
                  : (editingLocation ? 'Save changes' : 'Add location')}
              </Button>
              </>)}
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {/* ⚠️ MOVING THE HEAD OFFICE IS A DECISION, NOT A TOGGLE — Rob, 08/08/2026.
          The company record and this location can hold different addresses and different
          public holiday regions. Whichever one wins reaches the address printed on
          invoices and the bank holidays of everyone without an office of their own, so
          the difference is shown and the direction is CHOSEN. Before this, the move
          copied nothing and whichever screen was saved next silently won. */}
      <Dialog open={!!headOfficeMove} onOpenChange={(o) => !o && setHeadOfficeMove(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Make {headOfficeMove?.location?.name} your head office?
            </DialogTitle>
            <DialogDescription>
              Your company details and this location don&apos;t match. Which is right?
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="rounded-lg border border-border overflow-hidden">
              <div className="grid grid-cols-3 text-xs font-medium bg-secondary/50 px-3 py-2">
                <span />
                <span>Company now</span>
                <span>{headOfficeMove?.location?.name}</span>
              </div>
              {(headOfficeMove?.differences || []).map((d) => (
                <div key={d.field} className="grid grid-cols-3 text-sm px-3 py-2 border-t border-border/60">
                  <span className="text-muted-foreground">{d.label}</span>
                  <span className="truncate">{d.company || '—'}</span>
                  <span className="truncate font-medium">{d.location || '—'}</span>
                </div>
              ))}
            </div>

            {/* ⚠️ THE NUMBER THAT MAKES THIS REAL. The company's region is the fallback
                for anyone without an office, so a region change here is somebody's leave.
                Only shown when the region is actually one of the differences. */}
            {headOfficeMove?.people_following_the_company > 0 && (
              <Alert>
                <AlertCircle className="w-4 h-4" />
                <AlertDescription className="text-sm">
                  <strong>{headOfficeMove.people_following_the_company}</strong>{' '}
                  {headOfficeMove.people_following_the_company === 1 ? 'person has' : 'people have'}{' '}
                  no office of their own, so they follow the company&apos;s public holidays.
                  Their balances are recalculated straight away either way.
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-col gap-2">
            <Button
              className="btn-primary w-full"
              disabled={movingHeadOffice}
              onClick={() => applyHeadOfficeMove(headOfficeMove.location, 'location_wins')}
              data-testid="head-office-location-wins"
            >
              We&apos;ve moved — use {headOfficeMove?.location?.name}&apos;s details
            </Button>
            <Button
              variant="outline"
              className="w-full"
              disabled={movingHeadOffice}
              onClick={() => applyHeadOfficeMove(headOfficeMove.location, 'company_wins')}
              data-testid="head-office-company-wins"
            >
              Keep our company details and update {headOfficeMove?.location?.name}
            </Button>
            <Button variant="ghost" className="w-full" disabled={movingHeadOffice}
                    onClick={() => setHeadOfficeMove(null)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete location</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{locationToDelete?.name}"?
            </DialogDescription>
          </DialogHeader>
          
          {locationToDelete?.user_count > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                This location has {locationToDelete.user_count} employee{locationToDelete.user_count !== 1 ? 's' : ''} assigned. 
                Please reassign them to another location before deleting.
              </AlertDescription>
            </Alert>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleConfirmDelete}
              disabled={locationToDelete?.user_count > 0}
              data-testid="confirm-delete-location-btn"
            >
              Delete Location
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default LocationsPage;
